import {
  createTrancheEditRoute,
  SUBSCRIPTION_FUNNEL,
} from 'adminConstants/routes';
import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getQueryKey } from '@trpc/react-query';
import AlertDialog from 'components/AlertDialog';
import Button from 'components/Button';
import { UploadedFile } from 'components/Dropzone';
import GridContainer from 'components/GridContainer';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import TrancheSelector from 'components/TrancheSelector';
import { RolePermission } from 'further-types/admin-user-role';
import { UploadType } from 'further-types/files';
import { FundStatus, TrancheVisibility } from 'further-types/fund';
import { TagName } from 'further-types/tag';
import { SortOrder } from 'further-types/utils';
import { useDisclosure } from 'further-ui/hooks';
import { useFunds } from 'hooks/data/fund/useFunds';
import useListPendingAdvisedForFund from 'hooks/data/investment/useListPendingAdvisedForFund';
import useReassignOrDeletePendingAdvised from 'hooks/data/investment/useReassignOrDeletePendingAdvised';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useGetRole } from 'hooks/ui/useGetRole';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import useSearchParams from 'hooks/ui/useSearchParams';
import api, { ApiInputs } from 'lib/trpcClient';
import React, { useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import { TrancheData } from '../types';
import useTrancheForm, {
  TrancheFormInput,
  trancheFormSchema,
  tranchePublishableSchema,
} from '../useTrancheForm';
import useUsesDealDocumentation from '../useUsesDealDocumentation';
import AccordionItem from './AccordionItem';
import AdditionalInformation from './AdditionalInformation';
import AdHocAdjustmentTooltip from './AdHocAdjustmentTooltip';
import BankDetails from './BankDetails';
import Banner from './Banner';
import BasicInformation from './BasicInformation';
import ConfirmationEmailCustomisation from './ConfirmationEmailCustomisation';
import FeeManagement from './FeeManagement';
import FinalDealToggleAndLog from './FinalDealToggleAndLog';
import TrancheDataSnapshot from './TrancheDataSnapshot';
import TranchePromotionInformation from './TrancheInvestmentDocumentation';

// this will continue to be required on this v1 form but will not be required on v2
// mirrors TrancheDataSnapshot's tag-specific requiredness: LP omits incomeTaxRebate, VCT omits targetReturn/targetPortfolioSize
const buildKeyDataSchemaForTag = (tagName: string | undefined) =>
  z.object({
    minimumDirectInvestment: z
      .string()
      .min(1, 'Please enter minimum direct subscription.'),
    minimumAdvisedInvestment: z
      .string()
      .min(1, 'Please enter minimum advised subscription.'),
    incomeTaxRebate:
      tagName !== TagName.LP
        ? z.string().min(1, 'Please enter income tax relief.')
        : z.string(),
    targetReturn:
      tagName !== TagName.VCT
        ? z.string().min(1, 'Please select target return.')
        : z.string(),
    targetPortfolioSize:
      tagName !== TagName.VCT
        ? z.string().min(1, 'Please enter target portfolio size.')
        : z.string(),
  });

const toFile = (file?: { name: string; path: string } | null) =>
  file ? { name: file.name, path: file.path } : null;

const buildBaseTranchePayload = (tranche: TrancheData | undefined) => ({
  id: tranche?.id ?? '',
  firmId: tranche?.firmId ?? '',
  fundName: tranche?.fundName ?? '',
  fundStatus: tranche?.fundStatus ?? FundStatus.Draft,
  startDate: tranche?.startDate ?? undefined,
  closedDate: tranche?.closedDate ?? undefined,
  finishDate: tranche?.finishDate,
  tag: tranche?.tag ?? '',
  isProfessionalClientsOnly: tranche?.isProfessionalClientsOnly ?? false,
  trancheVisibility: tranche?.trancheVisibility ?? TrancheVisibility.ShowOnFirm,
  fundRaiseLimit: tranche?.fundRaiseLimit ?? undefined,
  incomeTaxRebate: tranche?.incomeTaxRebate,
  targetReturn: tranche?.targetReturn,
  targetPortfolioSize: tranche?.targetPortfolioSize,
  minimumDirectInvestment: tranche?.minimumDirectInvestment,
  minimumAdvisedInvestment: tranche?.minimumAdvisedInvestment,
  minimumIncludesAdviceFee: tranche?.minimumIncludesAdviceFee,
  applicationFormFile: toFile(tranche?.applicationFormFile),
  prospectusFile: toFile(tranche?.prospectusFile),
  keyInfoFile: toFile(tranche?.keyInfoFile),
  defaultCoreFee: tranche?.coreFeeTemplateId || undefined,
  defaultPerformanceFee: tranche?.performanceFeeTemplateId || undefined,
  firmBankDetailId: tranche?.firmBankDetailId || undefined,
  title: tranche?.title || undefined,
  customBankTransferReference:
    tranche?.customBankTransferReference || undefined,
  whatToExpectNow: tranche?.whatToExpectNow,
  additionalAttachment: tranche?.additionalAttachment
    ? {
        name: tranche.additionalAttachment.name,
        path: tranche.additionalAttachment.path,
        preview: tranche.additionalAttachment.preview,
      }
    : null,
  additionalInfo: tranche?.additionalInfo,
});

const StyledCloseTrancheModalSubtitle = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledSubscriptionFunnelLink = styled(Link)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.green,
}));

const useStyles = makeStyles()(() => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  inlineContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
}));

type Props = {
  tranche?: TrancheData;
  isLoadingTranche: boolean;
};

const AddUpdateTranche: React.FC<Props> = ({ tranche, ...props }) => {
  const id = tranche?.id ?? '';
  const history = useHistory();
  const create = api.fund.create.useMutation(
    useHandleTrpcMutation()({
      invalidations: [['tranches']],
    }),
  );
  const trpcEdit = api.fund.update.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Tranche updated successfully.',
      invalidations: [['tranches'], getQueryKey(api.fund.get)],
    }),
  );
  const isUpdating = create.isPending || trpcEdit.isPending;
  const { isLoadingTranche } = props;
  const { firmId: usersFirmId } = useGetRole();
  const [updatePermission] = useHasPermissions([RolePermission.EditTranche]);
  const { error: showError } = useNotification();
  const { uploadFile } = useFileUpload();
  const updateFeeManagement = useDisclosure();
  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Tranches', link: '/tranche' },
    {
      label: id ? 'Update Tranche' : 'Add Tranche',
      link: '/',
      isActive: true,
    },
  ];

  const tags = api.eisTag.list.useQuery({});
  const { searchParams, setSearchParam } = useSearchParams();
  const rawPanel = searchParams.get('expandedPanel');
  const expanded: string | false =
    rawPanel === 'none' ? false : rawPanel ?? 'basicInformation';
  const setExpanded = (panel: string | false) => {
    setSearchParam('expandedPanel', panel || 'none');
  };
  const { classes } = useStyles();

  const hasKiCertificate = tranche?.hasKiCertificate ?? false;

  const form = useTrancheForm({
    firmId: usersFirmId,
    values: tranche
      ? {
          firmId: tranche.firmId,
          fundName: tranche.fundName,
          fundStatus: tranche.fundStatus ?? FundStatus.Draft,
          startDate: tranche.startDate ?? null,
          finishDate: tranche.finishDate ?? null,
          closedDate: tranche.closedDate ?? null,
          tag: tranche.tag ?? '',
          isProfessionalClientsOnly: tranche.isProfessionalClientsOnly ?? false,
          trancheVisibility:
            tranche.trancheVisibility ?? TrancheVisibility.ShowOnFirm,
          fundRaiseLimit: tranche.fundRaiseLimit ?? null,
          incomeTaxRebate: tranche.incomeTaxRebate ?? '',
          targetReturn: tranche.targetReturn ?? '',
          targetPortfolioSize: tranche.targetPortfolioSize ?? '',
          minimumDirectInvestment: tranche.minimumDirectInvestment ?? '',
          minimumAdvisedInvestment: tranche.minimumAdvisedInvestment ?? '',
          minimumIncludesAdviceFee: tranche.minimumIncludesAdviceFee ?? false,
          applicationFormFile: tranche.applicationFormFile
            ? {
                fileName: tranche.applicationFormFile.name,
                filePath: tranche.applicationFormFile.path,
                previewUri: tranche.applicationFormFile.preview,
              }
            : null,
          prospectusFile: tranche.prospectusFile
            ? {
                fileName: tranche.prospectusFile.name,
                filePath: tranche.prospectusFile.path,
                previewUri: tranche.prospectusFile.preview,
              }
            : null,
          keyInfoFile: tranche.keyInfoFile
            ? {
                fileName: tranche.keyInfoFile.name,
                filePath: tranche.keyInfoFile.path,
                previewUri: tranche.keyInfoFile.preview,
              }
            : null,
          additionalDocuments: (tranche.additionalDocuments ?? []).map(
            (doc) => ({
              fileName: doc.name,
              filePath: doc.path,
              previewUri: doc.preview,
            }),
          ),
          defaultCoreFee: tranche.coreFeeTemplateId ?? '',
          defaultPerformanceFee: tranche.performanceFeeTemplateId ?? '',
          firmBankDetailId: tranche.firmBankDetailId ?? '',
          title: tranche.title ?? '',
          customBankTransferReference:
            tranche.customBankTransferReference ?? '',
          whatToExpectNow: tranche.whatToExpectNow ?? '',
          additionalAttachment: tranche.additionalAttachment
            ? {
                fileName: tranche.additionalAttachment.name,
                filePath: tranche.additionalAttachment.path,
                previewUri: tranche.additionalAttachment.preview,
              }
            : null,
          additionalInfo: tranche.additionalInfo ?? '',
        }
      : undefined,
  });
  const watchedFirmId = form.watch('firmId');
  const watchedTag = form.watch('tag');
  const watchedApplicationFormFile = form.watch('applicationFormFile');
  const watchedAdditionalDocuments = form.watch('additionalDocuments');
  const watchedFirmBankDetailId = form.watch('firmBankDetailId');
  const isDraft = tranche?.fundStatus === FundStatus.Draft;
  const selectedTag = tags.data?.find((t) => t.id === watchedTag);
  const usesDealDocumentation = useUsesDealDocumentation(watchedTag);
  const tagName = selectedTag?.name;
  const hasPrerequisitesForPublishing =
    tranche &&
    !!watchedFirmBankDetailId &&
    (usesDealDocumentation || !!watchedApplicationFormFile);

  const validateForOpenStatus = useCallback(
    (fundStatus: FundStatus): boolean => {
      if (fundStatus !== FundStatus.Open) return true;
      const result = tranchePublishableSchema.safeParse({
        ...form.getValues(),
        usesDealDocumentation,
      });
      if (!result.success) {
        const isFormField = (field: unknown): field is keyof TrancheFormInput =>
          typeof field === 'string' && field in trancheFormSchema.shape;
        result.error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (isFormField(field)) {
            form.setError(field, { message: issue.message });
          }
        });
        showError(
          'One or more fields have errors, please correct them and try again.',
        );
        return false;
      }
      return true;
    },
    [form, usesDealDocumentation, showError],
  );

  const investments = tranche?.investments ?? [];
  const closeTrancheDisclosure = useDisclosure<{
    requestPayload: ApiInputs['fund']['update'];
    targetFundId?: string | null;
  }>();
  const pendingAdvised = useListPendingAdvisedForFund(id);
  const reassignOrDeleteMutation = useReassignOrDeletePendingAdvised();
  const { firmId } = useGetRole();
  const { tranches } = useFunds({
    firmId: watchedFirmId || firmId,
    disabled: !closeTrancheDisclosure.isOpen,
  });

  const handleClick = async (isBannerClick?: boolean) => {
    await form.handleSubmit(async (data) => {
      const requestPayload: ApiInputs['fund']['update'] = {
        ...buildBaseTranchePayload(tranche),
        firmId: data.firmId,
        fundName: data.fundName,
        fundStatus: isBannerClick ? FundStatus.Open : data.fundStatus,
        startDate: data.startDate ?? undefined,
        closedDate: data.closedDate ?? undefined,
        finishDate: data.finishDate,
        tag: data.tag,
        isProfessionalClientsOnly: data.isProfessionalClientsOnly,
        trancheVisibility: data.trancheVisibility,
        fundRaiseLimit: data.fundRaiseLimit ?? undefined,
      };

      if (!validateForOpenStatus(requestPayload.fundStatus)) return;

      const isClosingTranche =
        id &&
        requestPayload.fundStatus === FundStatus.Closed &&
        tranche?.fundStatus !== FundStatus.Closed;

      if (isClosingTranche) {
        const pendingData =
          pendingAdvised.data ?? (await pendingAdvised.refetch())?.data;
        if (pendingData && pendingData.count > 0) {
          closeTrancheDisclosure.stageAction({
            requestPayload,
            targetFundId: null,
          });
          return;
        }
      }

      if (id) {
        trpcEdit.mutate({ ...requestPayload });
        if (isBannerClick) {
          form.setValue('fundStatus', FundStatus.Open);
        }
      } else {
        const { id: _id, fundStatus, ...rest } = requestPayload;
        if (fundStatus === FundStatus.Open) return;
        create.mutate(
          { ...rest, fundStatus },
          {
            onSuccess: (data) => {
              const editRoute = createTrancheEditRoute(data.id);
              history.push(
                isBannerClick
                  ? editRoute
                  : `${editRoute}?expandedPanel=trancheDataSnapshot`,
              );
            },
          },
        );
        return;
      }

      if (!isBannerClick) setExpanded('trancheDataSnapshot');
    })();
  };

  const handleConfirmCloseTranche = async () => {
    const { requestPayload, targetFundId } = closeTrancheDisclosure.actionProps;
    if (!id || !requestPayload) return;

    // Reassign or delete pending advised applications
    await reassignOrDeleteMutation.mutateAsync({
      sourceFundId: id,
      targetFundId: targetFundId || null,
    });

    trpcEdit.mutate(requestPayload);
    closeTrancheDisclosure.onClose();
  };

  const handelClickAdditional = () => {
    const safeParseResult = trancheFormSchema.safeParse({
      ...form.getValues(),
    });

    if (!safeParseResult.success) {
      const { fieldErrors } = safeParseResult.error.flatten();
      (Object.keys(fieldErrors) as Array<keyof typeof fieldErrors>).forEach(
        (field, index) => {
          form.setError(
            field,
            { message: fieldErrors[field]?.[0] },
            { shouldFocus: index === 0 },
          );
        },
      );
      return;
    }
    trpcEdit.mutate(
      {
        ...buildBaseTranchePayload(tranche),
        finishDate: safeParseResult.data.finishDate,
        additionalInfo: safeParseResult.data.additionalInfo,
      },
      { onSuccess: () => setExpanded('confirmationEmailCustomisation') },
    );
  };

  const handleClickKeyData = () => {
    if (!tranche?.finishDate) return;
    const formData = form.getValues();
    const result = buildKeyDataSchemaForTag(tagName).safeParse(formData);
    if (!result.success) {
      const { fieldErrors } = result.error.flatten();
      (Object.keys(fieldErrors) as Array<keyof typeof fieldErrors>).forEach(
        (field, index) => {
          form.setError(
            field,
            { message: fieldErrors[field]?.[0] },
            { shouldFocus: index === 0 },
          );
        },
      );
      return;
    }
    if (!validateForOpenStatus(tranche.fundStatus ?? FundStatus.Draft)) return;
    trpcEdit.mutate(
      {
        ...buildBaseTranchePayload(tranche),
        finishDate: tranche.finishDate,
        incomeTaxRebate: formData.incomeTaxRebate,
        targetReturn: formData.targetReturn,
        targetPortfolioSize: formData.targetPortfolioSize,
        minimumDirectInvestment: formData.minimumDirectInvestment,
        minimumAdvisedInvestment: formData.minimumAdvisedInvestment,
        minimumIncludesAdviceFee: formData.minimumIncludesAdviceFee,
      },
      { onSuccess: () => setExpanded('trancheInvestmentDocumentation') },
    );
  };

  const handleClickTranchePromotion = () => {
    if (!tranche?.finishDate) return;
    if (!validateForOpenStatus(tranche.fundStatus ?? FundStatus.Draft)) return;
    const formData = form.getValues();
    trpcEdit.mutate(
      {
        ...buildBaseTranchePayload(tranche),
        finishDate: tranche.finishDate,
        applicationFormFile: formData.applicationFormFile
          ? {
              name: formData.applicationFormFile.fileName,
              path: formData.applicationFormFile.filePath,
            }
          : null,
        prospectusFile: formData.prospectusFile
          ? {
              name: formData.prospectusFile.fileName,
              path: formData.prospectusFile.filePath,
            }
          : null,
        keyInfoFile: formData.keyInfoFile
          ? {
              name: formData.keyInfoFile.fileName,
              path: formData.keyInfoFile.filePath,
            }
          : null,
        additionalDocuments: formData.additionalDocuments.map((doc) => ({
          name: doc.fileName,
          path: doc.filePath,
          preview: doc.previewUri ?? '',
        })),
      },
      { onSuccess: () => setExpanded('feeManagement') },
    );
  };

  const handleAttachmentDrop = async (files: Array<UploadedFile>) => {
    if (!files[0]?.path) return;
    const { data, error } = await uploadFile(
      files[0],
      UploadType.FundDocuments,
    );
    if (data) {
      form.setValue('additionalAttachment', {
        fileName: files[0].name,
        filePath: data.filePath,
        previewUri: data.url,
      });
    } else {
      showError(error.message);
    }
  };

  const handleAttachmentRemove = () => {
    form.setValue('additionalAttachment', null);
  };

  const handleClickConfirmationEmailCustomisation = () => {
    if (!tranche?.finishDate) return;
    const { whatToExpectNow, additionalAttachment } = form.getValues();
    trpcEdit.mutate(
      {
        ...buildBaseTranchePayload(tranche),
        finishDate: tranche.finishDate,
        whatToExpectNow,
        additionalAttachment: additionalAttachment
          ? {
              name: additionalAttachment.fileName,
              path: additionalAttachment.filePath,
              preview: additionalAttachment.previewUri,
            }
          : null,
      },
      { onSuccess: () => setExpanded('trancheHighlights') },
    );
  };

  const handleClickBank = () => {
    if (!tranche?.finishDate) return;
    if (!validateForOpenStatus(tranche.fundStatus ?? FundStatus.Draft)) return;
    const { firmBankDetailId, title, customBankTransferReference } =
      form.getValues();
    trpcEdit.mutate(
      {
        ...buildBaseTranchePayload(tranche),
        finishDate: tranche.finishDate,
        firmBankDetailId,
        title,
        customBankTransferReference,
      },
      { onSuccess: () => setExpanded('additionalInformation') },
    );
  };

  const handleChange = (panel) => (_, isExpanded) => {
    if (id) {
      setExpanded(isExpanded ? panel : false);
    }
  };
  const handleCancel = () => {
    history.push('/tranche');
  };

  const { data: coreFeeTemplates, refetch: refetchCoreFeeTemplates } =
    api.coreFeeTemplates.list.useQuery(
      {
        orderBy: 'createdAt',
        order: SortOrder.Desc,
        firmId: watchedFirmId,
      },
      { refetchOnWindowFocus: true, enabled: expanded === 'feeManagement' },
    );

  const {
    data: performanceFeeTemplates,
    refetch: refetchPerformanceFeeTemplates,
  } = api.performanceFeeTemplates.list.useQuery(
    {
      orderBy: 'createdAt',
      order: SortOrder.Desc,
      firmId: watchedFirmId,
    },
    { refetchOnWindowFocus: true, enabled: expanded === 'feeManagement' },
  );

  const handleChangeFeeManagement = () => {
    const { defaultCoreFee, defaultPerformanceFee } = form.getValues();
    // If there are investments in tranche, we need to show a warning modal
    if (investments.length > 0) {
      updateFeeManagement.onOpen();
    } else {
      if (!tranche?.finishDate) return;
      trpcEdit.mutate(
        {
          ...buildBaseTranchePayload(tranche),
          finishDate: tranche.finishDate,
          defaultCoreFee,
          defaultPerformanceFee,
        },
        { onSuccess: () => setExpanded('bankDetails') },
      );
    }
  };

  return (
    <>
      <PageContainer
        heading={id ? 'Tranche: Tranche details' : 'Add Tranche'}
        breadcrumbs={breadcrumbs}
        loading={isLoadingTranche}
      >
        <PageContent>
          {!isLoadingTranche && (isDraft || !tranche) && (
            <Banner
              hasPrerequisitesForPublishing={!!hasPrerequisitesForPublishing}
              handleOpenFund={() => {
                handleClick(true);
              }}
            />
          )}

          <AccordionItem
            expanded={expanded}
            heading="Basic information"
            handleChange={handleChange}
            panelName="basicInformation"
            chip={!id ? 'Required' : undefined}
          >
            <BasicInformation
              control={form.control}
              handleCancel={handleCancel}
              handleClick={handleClick}
              id={id}
              investment={investments}
              tagList={tags.data ?? []}
              buttonDisabled={isUpdating}
              hasPrerequisitesForPublishing={
                !!watchedFirmBankDetailId &&
                (usesDealDocumentation || !!watchedApplicationFormFile)
              }
              usesDealDocumentation={usesDealDocumentation}
              viewOnly={!updatePermission}
            />
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Tranche data snapshot"
            handleChange={handleChange}
            panelName="trancheDataSnapshot"
            disabled={!id}
          >
            <TrancheDataSnapshot
              control={form.control}
              handleCancel={handleCancel}
              disabled={isUpdating}
              handleClickKeyData={handleClickKeyData}
              id={id}
              tagName={tagName}
              viewOnly={!updatePermission}
            />
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Tranche subscription documentation"
            handleChange={handleChange}
            panelName="trancheInvestmentDocumentation"
            disabled={!id}
            chip={
              !id ||
              (isDraft && !usesDealDocumentation && !watchedApplicationFormFile)
                ? 'Required'
                : undefined
            }
          >
            <TranchePromotionInformation
              control={form.control}
              handleCancel={handleCancel}
              handleClickTranchePromotion={handleClickTranchePromotion}
              id={id}
              disabled={isUpdating}
              usesDealDocumentation={usesDealDocumentation}
              isLP={tagName === TagName.LP}
              viewOnly={!updatePermission}
              additionalDocuments={watchedAdditionalDocuments ?? []}
              onChangeAdditionalDocuments={(value) =>
                form.setValue('additionalDocuments', value, {
                  shouldDirty: true,
                })
              }
            />
          </AccordionItem>
          <AccordionItem
            expanded={expanded}
            heading="Fee management"
            handleChange={handleChange}
            panelName="feeManagement"
            disabled={!id}
          >
            <GridContainer>
              <Grid size={{ xs: 12 }}>
                <FeeManagement
                  control={form.control}
                  coreFeeTemplates={coreFeeTemplates?.result ?? []}
                  performanceFeeTemplates={
                    performanceFeeTemplates?.result ?? []
                  }
                  refetchCoreFeeTemplates={refetchCoreFeeTemplates}
                  refetchPerformanceFeeTemplates={
                    refetchPerformanceFeeTemplates
                  }
                  disabled={!updatePermission}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <div className={classes.inlineContainer}>
                  <div>
                    <AdHocAdjustmentTooltip />
                  </div>
                  {updatePermission && (
                    <div className={classes.buttonContainer}>
                      <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.marginLeftBtn}
                        disabled={isUpdating}
                        color="primary"
                        onClick={handleChangeFeeManagement}
                      >
                        {id ? 'Update' : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
              </Grid>
            </GridContainer>
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Bank details"
            handleChange={handleChange}
            panelName="bankDetails"
            disabled={!id}
            chip={
              !id || (isDraft && !watchedFirmBankDetailId)
                ? 'Required'
                : undefined
            }
          >
            <BankDetails
              control={form.control}
              handleCancel={handleCancel}
              handleClickBank={handleClickBank}
              id={id}
              buttonDisabled={isUpdating}
              formDisabled={!updatePermission}
              firmId={watchedFirmId}
            />
          </AccordionItem>
          <AccordionItem
            expanded={expanded}
            heading="Additional information"
            handleChange={handleChange}
            panelName="additionalInformation"
            disabled={!id}
          >
            <AdditionalInformation
              control={form.control}
              handleCancel={handleCancel}
              buttonDisabled={isUpdating}
              formDisabled={!updatePermission}
              handelClickAdditional={handelClickAdditional}
              id={id}
            />
          </AccordionItem>
          <AccordionItem
            expanded={expanded}
            heading="Confirmation email customisation"
            handleChange={handleChange}
            panelName="confirmationEmailCustomisation"
            disabled={!id}
          >
            <ConfirmationEmailCustomisation
              control={form.control}
              handleCancel={handleCancel}
              buttonDisabled={isUpdating}
              formDisabled={!updatePermission}
              handleClickConfirmationEmailCustomisation={
                handleClickConfirmationEmailCustomisation
              }
              onAttachmentDrop={handleAttachmentDrop}
              onAttachmentRemove={handleAttachmentRemove}
              id={id}
            />
          </AccordionItem>

          <AccordionItem
            expanded={expanded}
            heading="Final deal"
            handleChange={handleChange}
            panelName="finalDeal"
            disabled={!id}
          >
            {/* This one does not need a "disabled" as it's handled internally by this component. */}
            <FinalDealToggleAndLog
              hasKiCertficate={hasKiCertificate}
              fundId={id}
              viewOnly={!updatePermission}
            />
          </AccordionItem>
          <AlertDialog
            onClose={updateFeeManagement.onClose}
            open={updateFeeManagement.isOpen}
            title="Warning"
            content={
              <>
                <b>Please note:</b> Changes to default fee settings will only
                apply to future subscriptions. To update existing subscriptions
                in this tranche, please navigate to individual subscription
                pages and edit the selected fee preset
              </>
            }
            btnLabels={{ cancel: 'Go back', confirm: 'Update' }}
            cancelBtnProps={{ color: 'primary', variant: 'outlined' }}
            confirmBtnProps={{
              color: 'primary',
            }}
            onConfirm={() => {
              if (!tranche?.finishDate) return;
              const { defaultCoreFee, defaultPerformanceFee } =
                form.getValues();
              trpcEdit.mutate(
                {
                  ...buildBaseTranchePayload(tranche),
                  finishDate: tranche.finishDate,
                  defaultCoreFee,
                  defaultPerformanceFee,
                },
                { onSuccess: () => setExpanded('bankDetails') },
              );
              updateFeeManagement.onClose();
            }}
          />
          <AlertDialog
            open={closeTrancheDisclosure.isOpen}
            onClose={closeTrancheDisclosure.onClose}
            title="Live advised applications"
            content={
              <>
                <StyledCloseTrancheModalSubtitle>
                  There are pending advised applications for this tranche which
                  you can view on the{' '}
                  <StyledSubscriptionFunnelLink
                    to={SUBSCRIPTION_FUNNEL}
                    target="_blank"
                  >
                    Subscription Funnel
                  </StyledSubscriptionFunnelLink>{' '}
                  page. Select a new tranche for these applications below. If no
                  tranche is selected, the system will delete pending
                  applications.
                </StyledCloseTrancheModalSubtitle>
                <TrancheSelector
                  firmId={watchedFirmId}
                  openOnly
                  selectedTranche={
                    closeTrancheDisclosure.actionProps.targetFundId &&
                    tranches.data
                      ? tranches.data.find(
                          ({ _id }) =>
                            _id ===
                            closeTrancheDisclosure.actionProps.targetFundId,
                        ) ?? null
                      : null
                  }
                  onChange={(tranche) => {
                    closeTrancheDisclosure.stageAction({
                      ...closeTrancheDisclosure.actionProps,
                      targetFundId: tranche?._id ?? null,
                    });
                  }}
                />
              </>
            }
            btnLabels={{
              cancel: 'Go back',
              confirm: 'Close tranche',
            }}
            onConfirm={handleConfirmCloseTranche}
            confirmBtnProps={{
              disabled:
                reassignOrDeleteMutation.isPending ||
                isUpdating ||
                !pendingAdvised.data,
              loading: reassignOrDeleteMutation.isPending || isUpdating,
            }}
          />
        </PageContent>
      </PageContainer>
    </>
  );
};
export default AddUpdateTranche;
